/**
 * Theme Detection Utility
 *
 * Shared theme detection logic used by both:
 * 1. Inline script in index.html (FOUC prevention)
 * 2. ThemeContext (runtime theme management)
 */

/**
 * Detect the user's theme preference
 * Priority: localStorage > system preference > default (light)
 *
 * @returns {'light'|'dark'} - The detected theme
 */
export function detectTheme() {
  // 1. Check localStorage for saved preference
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme === 'light' || savedTheme === 'dark') {
    return savedTheme;
  }

  // 2. Check system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  // 3. Default to light theme
  return 'light';
}

/**
 * Save theme preference to localStorage
 * @param {'light'|'dark'} theme - The theme to save
 */
export function saveTheme(theme) {
  if (theme !== 'light' && theme !== 'dark') {
    // Invalid theme - ignore silently
    return;
  }

  try {
    localStorage.setItem('theme', theme);
  } catch (_error) {
    // Failed to save theme - non-fatal, ignore
  }
}

/**
 * Listen for system theme changes and call callback
 * @param {Function} callback - Called with new theme ('light' | 'dark')
 * @returns {Function} - Cleanup function to remove listener
 */
export function watchSystemTheme(callback) {
  if (!window.matchMedia) {
    return () => {}; // No-op cleanup
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handler = e => {
    const newTheme = e.matches ? 'dark' : 'light';
    callback(newTheme);
  };

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }

  // Older browsers
  mediaQuery.addListener(handler);
  return () => mediaQuery.removeListener(handler);
}

export default {
  detectTheme,
  saveTheme,
  watchSystemTheme
};
